import {
  Bound,
  Currency,
  CurrencyAmount,
  encodeSqrtRatioX96,
  Field,
  getTickToPrice,
  INITIAL_POOL_FEE,
  nearestUsableTick,
  Pool,
  Position,
  Price,
  priceToClosestTick,
  TickMath,
  Token,
  tryParseAmount,
  tryParseTick,
  ZERO
} from '@cryptoalgebra/kim-sdk';
import { Address } from '@thirdweb-dev/sdk';
import { useAddress, useBalance } from '@thirdweb-dev/react';
import { useMemo } from 'react';
import { getCurrencyFromToken } from '@app/helpers/currency';
import { PoolState, usePool } from '@app/hooks/v3/usePool';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';
import { IDerivedMintInfo } from '@app/state/mintStore';

export function useDerivedManualMintInfo(
  currencyA?: Currency,
  currencyB?: Currency,
  poolAddress?: Address,
  feeAmount?: typeof INITIAL_POOL_FEE,
  baseCurrency?: Currency,
  existingPosition?: Position
): IDerivedMintInfo {
  const account = useAddress();

  const {
    independentField,
    typedValue,
    leftRangeTypedValue,
    rightRangeTypedValue,
    startPriceTypedValue
  } = useLiquidityWizardState();

  const dependentField =
    independentField === Field.CURRENCY_A ? Field.CURRENCY_B : Field.CURRENCY_A;

  // currencies
  const currencies: { [field in Field]?: Currency } = useMemo(
    () => ({
      [Field.CURRENCY_A]: currencyA,
      [Field.CURRENCY_B]: currencyB
    }),
    [currencyA, currencyB]
  );

  // formatted with tokens
  const [tokenA, tokenB, baseToken] = useMemo(
    () => [currencyA?.wrapped, currencyB?.wrapped, baseCurrency?.wrapped],
    [currencyA, currencyB, baseCurrency]
  );

  const [token0, token1] = useMemo(
    () =>
      tokenA && tokenB && !tokenA.equals(tokenB)
        ? tokenA.sortsBefore(tokenB)
          ? [tokenA, tokenB]
          : [tokenB, tokenA]
        : [undefined, undefined],
    [tokenA, tokenB]
  );

  const [addressA, addressB] = [
    getCurrencyFromToken(token0, currencyA, currencyB)?.isNative &&
    !existingPosition
      ? undefined
      : token0?.address || '',
    getCurrencyFromToken(token1, currencyA, currencyB)?.isNative &&
    !existingPosition
      ? undefined
      : token1?.address || ''
  ] as Address[];

  const { data: token0Balance } = useBalance(addressA);

  const { data: token1Balance } = useBalance(addressB);

  const currencyBalances: { [field in Field]?: CurrencyAmount<Currency> } = {
    [Field.CURRENCY_A]:
      token0 && token0Balance
        ? CurrencyAmount.fromRawAmount(token0, token0Balance.value.toString())
        : undefined,
    [Field.CURRENCY_B]:
      token1 && token1Balance
        ? CurrencyAmount.fromRawAmount(token1, token1Balance.value.toString())
        : undefined
  };

  const [poolState, pool] = usePool(poolAddress as Address);

  const noLiquidity = poolState === PoolState.NOT_EXISTS;

  const dynamicFee = pool ? pool.fee : 100;

  const tickSpacing = pool ? pool.tickSpacing : 60;

  // note to parse inputs in reverse
  const invertPrice = Boolean(baseToken && token0 && !baseToken.equals(token0));

  // always returns the price with 0 as base token
  const price: Price<Token, Token> | undefined = useMemo(() => {
    // if no liquidity use typed value
    if (noLiquidity) {
      const parsedQuoteAmount = tryParseAmount(
        startPriceTypedValue,
        invertPrice ? token0 : token1
      );

      if (parsedQuoteAmount && token0 && token1) {
        const baseAmount = tryParseAmount('1', invertPrice ? token1 : token0);
        const price =
          baseAmount && parsedQuoteAmount
            ? new Price(
                baseAmount.currency,
                parsedQuoteAmount.currency,
                baseAmount.quotient,
                parsedQuoteAmount.quotient
              )
            : undefined;

        return (invertPrice ? price?.invert() : price) ?? undefined;
      }

      return undefined;
    } else {
      // get the amount of quote currency
      return pool && token0 ? pool.priceOf(token0) : undefined;
    }
  }, [noLiquidity, startPriceTypedValue, invertPrice, token1, token0, pool]);

  // check for invalid price input (converts to invalid ratio)
  const invalidPrice = useMemo(() => {
    const sqrtRatioX96 = price
      ? encodeSqrtRatioX96(price.numerator, price.denominator)
      : undefined;

    return (
      price &&
      sqrtRatioX96 &&
      !(
        BigInt(sqrtRatioX96.toString()) >=
          BigInt(TickMath.MIN_SQRT_RATIO.toString()) &&
        BigInt(sqrtRatioX96.toString()) <
          BigInt(TickMath.MAX_SQRT_RATIO.toString())
      )
    );
  }, [price]);

  // used for ratio calculation when pool not initialized
  const mockPool = useMemo(() => {
    if (tokenA && tokenB && feeAmount && price && !invalidPrice) {
      const currentTick = priceToClosestTick(price);
      const currentSqrt = TickMath.getSqrtRatioAtTick(currentTick);

      return new Pool(
        tokenA,
        tokenB,
        feeAmount,
        currentSqrt,
        0,
        currentTick,
        60,
        []
      );
    } else {
      return undefined;
    }
  }, [feeAmount, invalidPrice, price, tokenA, tokenB]);

  // if pool exists use it, if not use the mock pool
  const poolForPosition: Pool | undefined = pool ?? mockPool;

  // lower and upper limits in the tick space
  const tickSpaceLimits: {
    [bound in Bound]: number | undefined;
  } = useMemo(
    () => ({
      [Bound.LOWER]: tickSpacing
        ? nearestUsableTick(TickMath.MIN_TICK, tickSpacing)
        : undefined,
      [Bound.UPPER]: tickSpacing
        ? nearestUsableTick(TickMath.MAX_TICK, tickSpacing)
        : undefined
    }),
    [tickSpacing]
  );

  // parse typed range values and determine closest ticks
  // lower should always be a smaller tick
  const ticks: {
    [key: string]: number | undefined;
  } = useMemo(() => {
    return {
      [Bound.LOWER]:
        typeof existingPosition?.tickLower === 'number'
          ? existingPosition.tickLower
          : (invertPrice && typeof rightRangeTypedValue === 'boolean') ||
            (!invertPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.LOWER]
          : invertPrice
          ? tryParseTick(
              token1,
              token0,
              rightRangeTypedValue.toString(),
              tickSpacing
            )
          : tryParseTick(
              token0,
              token1,
              leftRangeTypedValue.toString(),
              tickSpacing
            ),
      [Bound.UPPER]:
        typeof existingPosition?.tickUpper === 'number'
          ? existingPosition.tickUpper
          : (!invertPrice && typeof rightRangeTypedValue === 'boolean') ||
            (invertPrice && typeof leftRangeTypedValue === 'boolean')
          ? tickSpaceLimits[Bound.UPPER]
          : invertPrice
          ? tryParseTick(
              token1,
              token0,
              leftRangeTypedValue.toString(),
              tickSpacing
            )
          : tryParseTick(
              token0,
              token1,
              rightRangeTypedValue.toString(),
              tickSpacing
            )
    };
  }, [
    existingPosition,
    invertPrice,
    leftRangeTypedValue,
    rightRangeTypedValue,
    token0,
    token1,
    tickSpaceLimits,
    tickSpacing
  ]);

  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = ticks || {};

  // specifies whether the lower and upper ticks is at the exteme bounds
  const ticksAtLimit = useMemo(
    () => ({
      [Bound.LOWER]: feeAmount && tickLower === tickSpaceLimits.LOWER,
      [Bound.UPPER]: feeAmount && tickUpper === tickSpaceLimits.UPPER
    }),
    [tickSpaceLimits, tickLower, tickUpper, feeAmount]
  );

  // mark invalid range
  const invalidRange = Boolean(
    typeof tickLower === 'number' &&
      typeof tickUpper === 'number' &&
      tickLower >= tickUpper
  );

  // always returns the price with 0 as base token
  const pricesAtTicks = useMemo(() => {
    return {
      [Bound.LOWER]: getTickToPrice(token0, token1, ticks[Bound.LOWER]),
      [Bound.UPPER]: getTickToPrice(token0, token1, ticks[Bound.UPPER])
    };
  }, [token0, token1, ticks]);
  const { [Bound.LOWER]: lowerPrice, [Bound.UPPER]: upperPrice } =
    pricesAtTicks;

  // liquidity range warning
  const outOfRange = Boolean(
    !invalidRange &&
      price &&
      lowerPrice &&
      upperPrice &&
      (price.lessThan(lowerPrice) || price.greaterThan(upperPrice))
  );

  const independentAmount: CurrencyAmount<Currency> | undefined =
    tryParseAmount(typedValue, currencies[independentField]);

  const dependentAmount: CurrencyAmount<Currency> | undefined = useMemo(() => {
    // we wrap the currencies just to get the price in terms of the other token
    const wrappedIndependentAmount = independentAmount?.wrapped;
    const dependentCurrency =
      dependentField === Field.CURRENCY_B ? currencyB : currencyA;

    if (
      independentAmount &&
      wrappedIndependentAmount &&
      typeof tickLower === 'number' &&
      typeof tickUpper === 'number' &&
      poolForPosition
    ) {
      // if price is out of range or invalid range - return 0 (single deposit will be independent)
      if (outOfRange || invalidRange) {
        return undefined;
      }

      const position: Position | undefined =
        wrappedIndependentAmount.currency.equals(poolForPosition.token0)
          ? Position.fromAmount0({
              pool: poolForPosition,
              tickLower,
              tickUpper,
              amount0: independentAmount.quotient,
              useFullPrecision: true // we want full precision for the theoretical position
            })
          : Position.fromAmount1({
              pool: poolForPosition,
              tickLower,
              tickUpper,
              amount1: independentAmount.quotient
            });

      const dependentTokenAmount = wrappedIndependentAmount.currency.equals(
        poolForPosition.token0
      )
        ? position.amount1
        : position.amount0;

      return (
        dependentCurrency &&
        CurrencyAmount.fromRawAmount(
          dependentCurrency,
          dependentTokenAmount.quotient
        )
      );
    }

    return undefined;
  }, [
    independentAmount,
    outOfRange,
    dependentField,
    currencyB,
    currencyA,
    tickLower,
    tickUpper,
    poolForPosition,
    invalidRange
  ]);

  const parsedAmounts: {
    [field in Field]: CurrencyAmount<Currency> | undefined;
  } = useMemo(() => {
    return {
      [Field.CURRENCY_A]:
        independentField === Field.CURRENCY_A
          ? independentAmount
          : dependentAmount,
      [Field.CURRENCY_B]:
        independentField === Field.CURRENCY_A
          ? dependentAmount
          : independentAmount
    };
  }, [dependentAmount, independentAmount, independentField]);

  // single deposit only if price is out of range
  const deposit0Disabled = Boolean(
    typeof tickUpper === 'number' &&
      poolForPosition &&
      poolForPosition.tickCurrent >= tickUpper
  );
  const deposit1Disabled = Boolean(
    typeof tickLower === 'number' &&
      poolForPosition &&
      poolForPosition.tickCurrent <= tickLower
  );

  // sorted for token order
  const depositADisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled &&
        poolForPosition &&
        tokenA &&
        poolForPosition.token0.equals(tokenA)) ||
        (deposit1Disabled &&
          poolForPosition &&
          tokenA &&
          poolForPosition.token1.equals(tokenA))
    );
  const depositBDisabled =
    invalidRange ||
    Boolean(
      (deposit0Disabled &&
        poolForPosition &&
        tokenB &&
        poolForPosition.token0.equals(tokenB)) ||
        (deposit1Disabled &&
          poolForPosition &&
          tokenB &&
          poolForPosition.token1.equals(tokenB))
    );

  // create position entity based on users selection
  const position: Position | undefined = useMemo(() => {
    if (
      !poolForPosition ||
      !tokenA ||
      !tokenB ||
      typeof tickLower !== 'number' ||
      typeof tickUpper !== 'number' ||
      invalidRange
    ) {
      return undefined;
    }

    // mark as 0 if disabled because out of range
    const amount0 = !deposit0Disabled
      ? parsedAmounts?.[
          tokenA.equals(poolForPosition.token0)
            ? Field.CURRENCY_A
            : Field.CURRENCY_B
        ]?.quotient
      : ZERO;
    const amount1 = !deposit1Disabled
      ? parsedAmounts?.[
          tokenA.equals(poolForPosition.token0)
            ? Field.CURRENCY_B
            : Field.CURRENCY_A
        ]?.quotient
      : ZERO;

    if (amount0 !== undefined && amount1 !== undefined) {
      return Position.fromAmounts({
        pool: poolForPosition,
        tickLower,
        tickUpper,
        amount0,
        amount1,
        useFullPrecision: true // we want full precision for the theoretical position
      });
    } else {
      return undefined;
    }
  }, [
    parsedAmounts,
    poolForPosition,
    tokenA,
    tokenB,
    deposit0Disabled,
    deposit1Disabled,
    invalidRange,
    tickLower,
    tickUpper
  ]);

  let errorMessage: string | undefined;
  let errorCode: number | undefined;

  if (!account) {
    errorMessage = `Connect Wallet`;
    errorCode = 0;
  }

  if (poolState === PoolState.INVALID) {
    errorMessage = errorMessage ?? `Invalid pair`;
    errorCode = errorCode ?? 1;
  }

  if (invalidPrice) {
    errorMessage = errorMessage ?? `Invalid price input`;
    errorCode = errorCode ?? 2;
  }

  if (
    (!parsedAmounts[Field.CURRENCY_A] && !depositADisabled) ||
    (!parsedAmounts[Field.CURRENCY_B] && !depositBDisabled)
  ) {
    errorMessage = errorMessage ?? `Enter an amount`;
    errorCode = errorCode ?? 3;
  }

  if (!currencyA || !currencyB) {
    errorMessage = 'Select a pair';
    errorCode = errorCode ?? 3;
  }

  const {
    [Field.CURRENCY_A]: currencyAAmount,
    [Field.CURRENCY_B]: currencyBAmount
  } = parsedAmounts;

  const balanceA =
    currencyBalances.CURRENCY_A?.currency.wrapped.symbol ===
    currencyA?.wrapped.symbol
      ? currencyBalances.CURRENCY_A
      : currencyBalances.CURRENCY_B;

  if (currencyAAmount && balanceA?.lessThan(currencyAAmount)) {
    errorMessage = `Insufficient ${currencyA?.symbol} balance`;
    errorCode = errorCode ?? 4;
  }

  const balanceB =
    currencyBalances.CURRENCY_B?.currency.wrapped.address ===
    currencyB?.wrapped.address
      ? currencyBalances.CURRENCY_B
      : currencyBalances.CURRENCY_A;

  if (currencyBAmount && balanceB?.lessThan(currencyBAmount)) {
    errorMessage = `Insufficient ${currencyB?.symbol} balance`;
    errorCode = errorCode ?? 5;
  }

  const invalidPool = poolState === PoolState.INVALID;

  return {
    dependentField,
    currencies,
    pool,
    poolState,
    currencyBalances,
    parsedAmounts,
    ticks,
    price,
    pricesAtTicks,
    position,
    noLiquidity,
    errorMessage,
    errorCode,
    invalidPool,
    invalidRange,
    outOfRange,
    depositADisabled,
    depositBDisabled,
    invertPrice,
    ticksAtLimit,
    dynamicFee,
    lowerPrice,
    upperPrice,
    tickSpacing
  };
}
